import { computed, ComputedRef, ref } from "vue";

export interface DragClickDetectOptions {
  /**
   * Порог движения в пикселях для определения drag (по умолчанию 10)
   */
  dragThreshold?: number;

  /**
   * Порог времени в миллисекундах для определения drag (по умолчанию 200)
   */
  timeThreshold?: number;

  /**
   * CSS селекторы элементов, на которых нужно предотвратить клик
   * (по умолчанию: ".prevent-click, button, input, .q-btn")
   */
  excludeSelectors?: string;

  /**
   * Включить детекцию drag (по умолчанию true)
   */
  enabled?: boolean;
}

export interface DragClickDetectReturn {
  /**
   * Состояние: выполняется ли в данный момент drag
   */
  isDragging: ComputedRef<boolean>;

  /**
   * Обработчик mousedown события
   */
  onMouseDown: (e: MouseEvent) => void;

  /**
   * Обработчик mouseup события
   */
  onMouseUp: (e: MouseEvent) => void;

  /**
   * Обработчик touchstart события
   */
  onTouchStart: (e: TouchEvent) => void;

  /**
   * Обработчик touchend события
   */
  onTouchEnd: (e: TouchEvent) => void;

  /**
   * Функция-обертка для обработки клика с детекцией drag
   * Вызывает callback только если это был настоящий клик, а не drag
   */
  handleClick: (e: Event, callback: (e: Event) => void) => void;

  /**
   * Объект событий для v-on
   * Используйте getListeners(callback, options) для получения объекта событий
   * Если callback не передан, обработчики будут добавлены, но клик нужно обрабатывать вручную с проверкой isDragging
   */
  getListeners: (
    onClick?: (e: Event) => void,
    options?: { prevent?: boolean; stop?: boolean }
  ) => Record<string, any>;

  /**
   * Отменить следующий клик на карточке
   * Используйте в обработчиках вложенных кнопок для предотвращения открытия карточки
   */
  cancelClick: () => void;

  /**
   * Сброс состояния drag
   */
  reset: () => void;
}

/**
 * Универсальный композабл для детекции drag и блокировки ложных кликов при свайпе
 *
 * Упрощенная версия БЕЗ глобальных обработчиков. Использует флаг cancelNextClick
 * для отмены клика на карточке когда клик был по вложенной кнопке.
 *
 * ТРЕБОВАНИЕ: Вложенные кнопки НЕ должны использовать .stop модификатор
 * (событие должно всплывать до карточки для работы cancelClick)
 *
 * @example
 * // Базовое использование
 * ```vue
 * <template>
 *   <div v-on="dragListeners">
 *     <h3>{{ product.name }}</h3>
 *     <button @click.prevent="handleAddToCart">
 *       Добавить в корзину
 *     </button>
 *   </div>
 * </template>
 *
 * <script setup>
 * const { getListeners, cancelClick, isDragging } = useDragClickDetect();
 *
 * const openProduct = () => {
 *   console.log('Открываем карточку');
 * };
 *
 * const dragListeners = getListeners(openProduct, {
 *   prevent: true,
 *   stop: true,
 * });
 *
 * const handleAddToCart = () => {
 *   if (isDragging.value) return; // Проверка drag
 *   cancelClick(); // Отменяем клик на карточке
 *   addToCart();
 * };
 *
 * const addToCart = () => {
 *   console.log('Добавляем в корзину');
 * };
 * </script>
 * ```
 */
export function useDragClickDetect(
  options: DragClickDetectOptions = {}
): DragClickDetectReturn {
  const {
    dragThreshold = 10,
    timeThreshold = 200,
    excludeSelectors = ".prevent-click, button, input, .q-btn",
    enabled = true,
  } = options;

  const dragStart = ref<{ x: number; y: number; time: number } | null>(null);
  const wasDrag = ref<boolean>(false); // Флаг: был ли это drag (устанавливается синхронно)
  const cancelNextClick = ref<boolean>(false); // Флаг отмены следующего клика

  // isDragging теперь зависит от wasDrag (синхронно определяется в onMouseUp/onTouchEnd)
  const isDragging = computed(() => wasDrag.value);

  const onMouseDown = (e: MouseEvent) => {
    if (!enabled) return;
    dragStart.value = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now(),
    };
    wasDrag.value = false; // Сбрасываем флаг при начале нового взаимодействия
  };

  const onMouseUp = (e: MouseEvent) => {
    if (!enabled || !dragStart.value) return;

    // Синхронно определяем, был ли это drag или клик
    const deltaX = Math.abs(e.clientX - dragStart.value.x);
    const deltaY = Math.abs(e.clientY - dragStart.value.y);
    const deltaTime = Date.now() - dragStart.value.time;

    const isDrag =
      deltaX > dragThreshold ||
      deltaY > dragThreshold ||
      deltaTime > timeThreshold;

    // Устанавливаем флаг СИНХРОННО (до события click)
    wasDrag.value = isDrag;

    // Сбрасываем состояние с небольшой задержкой (cleanup)
    setTimeout(() => {
      dragStart.value = null;
      wasDrag.value = false;
    }, 50);
  };

  const onTouchStart = (e: TouchEvent) => {
    if (!enabled) return;
    const touch = e.touches[0];
    if (touch) {
      dragStart.value = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
      wasDrag.value = false; // Сбрасываем флаг при начале нового взаимодействия
    }
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (!enabled || !dragStart.value) return;

    const touch = e.changedTouches[0] || e.touches[0];
    if (!touch) {
      wasDrag.value = true; // Если нет touch - считаем drag
      setTimeout(() => {
        dragStart.value = null;
        wasDrag.value = false;
      }, 50);
      return;
    }

    // Синхронно определяем, был ли это drag или клик
    const deltaX = Math.abs(touch.clientX - dragStart.value.x);
    const deltaY = Math.abs(touch.clientY - dragStart.value.y);
    const deltaTime = Date.now() - dragStart.value.time;

    const isDrag =
      deltaX > dragThreshold ||
      deltaY > dragThreshold ||
      deltaTime > timeThreshold;

    // Устанавливаем флаг СИНХРОННО (до события click)
    wasDrag.value = isDrag;

    // Сбрасываем состояние с небольшой задержкой (cleanup)
    setTimeout(() => {
      dragStart.value = null;
      wasDrag.value = false;
    }, 50);
  };

  const cancelClick = () => {
    cancelNextClick.value = true;
    // Сбрасываем флаг асинхронно (после выполнения текущего клика)
    // Используем setTimeout(0) чтобы флаг сбросился после всплытия события
    setTimeout(() => {
      cancelNextClick.value = false;
    }, 0);
  };

  const handleClick = (e: Event, callback: (e: Event) => void) => {
    if (!enabled) {
      callback(e);
      return;
    }

    // Проверяем флаг отмены клика (от вложенных кнопок)
    if (cancelNextClick.value) {
      cancelNextClick.value = false;
      return;
    }

    if (!dragStart.value) {
      return;
    }

    // Проверяем флаг wasDrag (установлен синхронно в onMouseUp/onTouchEnd)
    if (wasDrag.value) {
      return; // Это был drag, не клик
    }

    // Проверяем excludeSelectors (дополнительная защита)
    const target = e.target as HTMLElement;
    if (excludeSelectors && target.closest(excludeSelectors)) {
      return;
    }

    callback(e);
  };

  const reset = () => {
    dragStart.value = null;
    wasDrag.value = false;
    cancelNextClick.value = false;
  };

  const getListeners = (
    onClick?: (e: Event) => void,
    options?: { prevent?: boolean; stop?: boolean }
  ) => {
    const { prevent = false, stop = false } = options || {};

    if (!enabled) {
      if (!onClick) return {};
      return {
        click: (e: Event) => {
          if (prevent) e.preventDefault();
          if (stop) e.stopPropagation();
          onClick(e);
        },
      };
    }

    const listeners: Record<string, any> = {
      mousedown: onMouseDown,
      mouseup: onMouseUp,
      touchstart: onTouchStart,
      touchend: onTouchEnd,
    };

    if (onClick) {
      listeners.click = (e: Event) => {
        if (prevent) e.preventDefault();
        if (stop) e.stopPropagation();
        handleClick(e, onClick);
      };
    }

    return listeners;
  };

  return {
    isDragging,
    onMouseDown,
    onMouseUp,
    onTouchStart,
    onTouchEnd,
    handleClick,
    getListeners,
    cancelClick,
    reset,
  };
}
